<script setup lang="ts">
  import { storeToRefs } from "pinia";
  import { useTalkRoomStore } from "~/stores/TalkRoomStore";
  import { useTalkRoomTmpViewStore } from "~/stores/TalkRoomTmpViewStore";

  interface Props {
    isSearching: boolean;
    isJumping: boolean;
    talkRoomId: string;
    topPx: number;
    leftPx: number;
    widthPx: number;
    isTest?: boolean; // テスト時のみ true にする
  }
  const props = defineProps<Props>();

  interface Emits {
    (event: "update:isSearching", isSearching: boolean): void;
    (event: "searchIdx", searchIdx: number | null): void;
    (event: "searchTalkId", searchTalkId: string | null): void;
    (event: "searchWord", searchWord: string | null): void;
  }
  const emit = defineEmits<Emits>();

  const isOpen = computed({
    get: () => props.isSearching,
    set: value => {
      emit("update:isSearching", value);
      if (!value) {
        // 閉じた場合に検索ワードハイライトをクリアさせるため
        showAndNotifyEmptySearch();
      }
    },
  });

  const talkRoomStore = useTalkRoomStore();
  const talkRoomTmpViewStore = useTalkRoomTmpViewStore();
  const SEARCH_COUNT_MAX = 100;

  // 検索状態
  const searchText = ref<string>("");
  const searchCount = ref<string>("0/0");
  const searchedWord = ref<string | null>(null);
  const searchedTalkIds = ref<string[] | null>(null);
  const searchedTalkIdIndex = ref<number>(0);

  // 検索開始・終了時
  watch(
    () => props.isSearching,
    () => {
      if (props.isSearching) {
        // 検索開始時に検索状態を初期化
        searchText.value = "";
        searchCount.value = "0/0";
        searchedWord.value = null;
        searchedTalkIds.value = null;
        searchedTalkIdIndex.value = 0;
      } else {
        // 検索終了時に検索ワードハイライトをクリアさせる
        showAndNotifyEmptySearch();
      }
    },
  );

  // 検索可能となったタイミング
  const { canSearch } = storeToRefs(talkRoomTmpViewStore);
  watch(canSearch, () => {
    if (canSearch.value && talkRoomTmpViewStore.searchText) {
      // 事前設定済みの検索ワードがあれば即時検索実行
      searchText.value = talkRoomTmpViewStore.searchText;
      talkRoomTmpViewStore.searchText = "";
      doSearch();
    }
  });

  // 検索実行
  const doSearch = async () => {
    if (props.isJumping) {
      // 移動中は無効 (連打抑止)
      return;
    }
    if (!canSearch.value) {
      // 検索可能フラグが落ちていたら無効 (初回表示待ち)
      return;
    }
    if (!canSearchTalk(searchText.value)) {
      // 検索可能なワードでなければクリアして終了
      showAndNotifyEmptySearch();
      return;
    }
    if (searchedTalkIds.value === null || searchedWord.value !== searchText.value) {
      // 初めての検索 or 検索ワードが変わった場合、検索APIでリストをもらう
      const talkIds = await talkRoomStore.searchTalkIds(props.talkRoomId, searchText.value, SEARCH_COUNT_MAX);
      if (!talkIds.length) {
        // 検索結果がなければクリアして終了
        showAndNotifyEmptySearch();
        return;
      }
      // 検索結果があれば設定して表示
      searchedTalkIds.value = talkIds;
      searchedWord.value = searchText.value;
      searchedTalkIdIndex.value = 0;
      showAndNotifySearch();
    } else {
      // 検索ずみ and 検索ワードが同じ場合、次の検索結果へ移動
      searchNext();
    }
  };

  // 検索結果で前へ移動
  const searchPrev = () => {
    if (!canMoveSearch()) {
      return;
    }
    searchedTalkIdIndex.value -= 1;
    if (searchedTalkIdIndex.value < 0) {
      searchedTalkIdIndex.value = searchedTalkIds.value!.length - 1; // 末尾へ移動
    }
    showAndNotifySearch();
  };

  // 検索結果で次へ移動
  const searchNext = () => {
    if (!canMoveSearch()) {
      return;
    }
    searchedTalkIdIndex.value += 1;
    if (searchedTalkIdIndex.value >= searchedTalkIds.value!.length) {
      searchedTalkIdIndex.value = 0; // 先頭へ戻る
    }
    showAndNotifySearch();
  };

  // 検索結果で移動可能か判定
  const canMoveSearch = () => {
    if (props.isJumping) {
      // 移動中は無効 (連打抑止)
      return false;
    }
    if (!searchedTalkIds.value || searchedTalkIds.value.length < 1) {
      // 検索結果がない場合は無効
      return false;
    }
    if (searchedWord.value !== searchText.value) {
      // 検索ワードが変わった場合は無効
      return false;
    }
    return true;
  };

  // 検索位置を表示し、親コンポーネントに対象トークIDとキーワードを通知
  const showAndNotifySearch = () => {
    searchCount.value = `${searchedTalkIdIndex.value + 1}/${searchedTalkIds.value!.length}`;
    emit("searchIdx", searchedTalkIdIndex.value);
    emit("searchTalkId", searchedTalkIds.value![searchedTalkIdIndex.value]);
    emit("searchWord", searchedWord.value!);
  };

  // 検索結果がないときの表示と通知
  const showAndNotifyEmptySearch = () => {
    searchCount.value = "0/0";
    emit("searchIdx", null);
    emit("searchTalkId", null);
    emit("searchWord", null);
  };

  const isTablet = isTabletAndSpDisplayRef();
  const searchWrapperTop = computed(() => props.topPx + "px");
  const searchWrapperLeft = computed(() => props.leftPx + "px");
  const searchWrapperWidth = computed(() => props.widthPx + "px");
</script>

<template>
  <v-dialog
    v-model="isOpen"
    class="search-dialog"
    :style="{
      top: searchWrapperTop,
      left: searchWrapperLeft,
      width: searchWrapperWidth,
    }"
    :scrim="false"
    :attach="!!isTest"
  >
    <div class="search-wrapper d-flex" data-testid="talkroom-detail-search">
      <v-text-field
        v-model="searchText"
        class="search-input"
        placeholder="キーワードを入力してください"
        prepend-inner-icon="$mdiMagnify"
        variant="solo"
        autofocus
        clearable
        @keyup.enter.exact="doSearch"
        @keyup.enter.shift.exact="searchPrev"
        @keyup.up="searchPrev"
        @keyup.down="searchNext"
      ></v-text-field>
      <div v-if="isTablet" class="search-next" @click="doSearch">
        <v-icon icon="$mdiChevronRight" />
      </div>
      <div class="search-count" data-testid="talkroom-search-count">
        {{ searchCount }}
      </div>
    </div>
  </v-dialog>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";

  .search-dialog {
    position: absolute;
    margin: 0;
    height: 66px;
    background: rgba(45, 45, 45, 1);
    padding: 0;
  }
  .search-wrapper {
    margin: -10px;
  }
  .search-input {
    height: 40px;
    width: 100%;
    padding: 0;
  }
  .search-next {
    margin-left: 8px;
    width: 40px;
    height: 40px;
    border-radius: 4px;
    background: rgb(var(--v-theme-monochrome-03));
    color: white;
    cursor: pointer;
    font-size: 22px;
    text-align: center;
  }
  .search-count {
    width: 40px;
    height: 100%;
    margin: 8px 0 8px 8px;
    text-align: center;
    color: white;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.8em;
  }
  :deep(.v-field--variant-solo) {
    background: rgb(var(--v-theme-monochrome-03));
    border-radius: 20px;
    height: 40px;
    @include display_tablet {
      border-radius: 4px;
    }
  }
  :deep(.v-field__field) {
    margin-top: -10px;
    height: 40px;
  }
</style>
